/**
 * Malay formative home-card helpers (Phase 2D.5).
 * Presentation only — mastery/evidence always come from the backend payload.
 */
import { ROUTES } from '../../utils/constants';
import { buildMalayAiTutorLiveUrl } from './malayAiTutorActivityRoute';
import type {
  FormativeHomeCard,
  NarrativeWritingHomeSlice,
} from './api/malayFormativeProgress.client';
import {
  buildNarrativeWritingWriteUrl,
  isOpaqueNarrativeDraftId,
} from './malayNarrativeWritingRoute';

export const MALAY_FORMATIVE_ACTIVITY_KEYS = [
  'malay_unit_5',
  'malay_jendela_seni_reading',
  'malay_narrative_learning_tutor',
] as const;

export type MalayFormativeActivityKey = (typeof MALAY_FORMATIVE_ACTIVITY_KEYS)[number];

export type MalayFormativeFeatureGate = {
  aiTutorEnabled: boolean;
  narrativeWritingEnabled: boolean;
  readingComprehensionEnabled?: boolean;
  grade?: number | null;
};

/** Grade 7 + reading_comprehension — matches Quick Actions and sidebar gating. */
export function isMalayReadingComprehensionAvailable(features: MalayFormativeFeatureGate): boolean {
  if (!features.readingComprehensionEnabled) return false;
  const gradeNum =
    typeof features.grade === 'number'
      ? features.grade
      : parseInt(String(features.grade ?? ''), 10);
  return Number.isFinite(gradeNum) && gradeNum === 7;
}

const ACTIVITY_LABEL_KEYS: Record<MalayFormativeActivityKey, string> = {
  malay_unit_5: 'dashboard.malayFormative.activities.unit5',
  malay_jendela_seni_reading: 'dashboard.malayFormative.activities.reading',
  malay_narrative_learning_tutor: 'dashboard.malayFormative.activities.narrative',
};

export function isMalayFormativeActivityKey(value: string): value is MalayFormativeActivityKey {
  return (MALAY_FORMATIVE_ACTIVITY_KEYS as readonly string[]).includes(value);
}

export function activityEnabledForFeatures(
  activityKey: string,
  features: MalayFormativeFeatureGate,
): boolean {
  if (activityKey === 'malay_narrative_learning_tutor') {
    return features.narrativeWritingEnabled;
  }
  if (activityKey === 'malay_jendela_seni_reading') {
    return features.aiTutorEnabled && isMalayReadingComprehensionAvailable(features);
  }
  if (activityKey === 'malay_unit_5') {
    return features.aiTutorEnabled;
  }
  return false;
}

export type NarrativeDualStatusKey = 'notStarted' | 'inProgress' | 'completed' | 'started';

export function resolveNarrativePlotStatusKey(
  slice: NarrativeWritingHomeSlice | null | undefined,
): NarrativeDualStatusKey {
  const raw = String(slice?.plot_planning_status || 'not_started').toLowerCase();
  switch (raw) {
    case 'completed':
    case 'complete':
      return 'completed';
    case 'in_progress':
    case 'in-progress':
      return 'inProgress';
    case 'started':
      return 'started';
    case 'not_started':
    case 'not-started':
    case '':
      return 'notStarted';
    default:
      return 'notStarted';
  }
}

export function resolveNarrativeDraftStatusKey(
  slice: NarrativeWritingHomeSlice | null | undefined,
): NarrativeDualStatusKey {
  const raw = String(slice?.story_draft_status || 'not_started').toLowerCase();
  switch (raw) {
    case 'completed':
    case 'complete':
      return 'completed';
    case 'in_progress':
    case 'in-progress':
      return 'inProgress';
    case 'started':
      return 'started';
    case 'not_started':
    case 'not-started':
    case '':
      return 'notStarted';
    default:
      return 'notStarted';
  }
}

export function narrativeWritingContinueRoute(
  card: FormativeHomeCard | null | undefined,
): string {
  const slice = card?.narrative_writing;
  const draftId = String(slice?.latest_draft_id || '').trim();
  if (draftId && isOpaqueNarrativeDraftId(draftId)) {
    return buildNarrativeWritingWriteUrl(draftId);
  }
  const draftStatus = resolveNarrativeDraftStatusKey(slice);
  if (draftStatus === 'started' || draftStatus === 'inProgress' || draftStatus === 'completed') {
    return ROUTES.STUDENT_NARRATIVE_WRITING_WRITE;
  }
  const plotStatus = resolveNarrativePlotStatusKey(slice);
  if (plotStatus === 'notStarted' && draftStatus === 'notStarted') {
    return ROUTES.STUDENT_NARRATIVE_WRITING;
  }
  return ROUTES.STUDENT_NARRATIVE_WRITING_WRITE;
}

export function isNarrativeWritingFullyComplete(
  card: FormativeHomeCard | null | undefined,
): boolean {
  const plotStatus = resolveNarrativePlotStatusKey(card?.narrative_writing);
  const draftStatus = resolveNarrativeDraftStatusKey(card?.narrative_writing);
  return plotStatus === 'completed' && draftStatus === 'completed';
}

export type NarrativeWritingCtaKey = 'start' | 'startWriting' | 'continueWriting' | 'completed';

export function narrativeWritingCtaKey(
  card: FormativeHomeCard | null | undefined,
): NarrativeWritingCtaKey {
  if (isNarrativeWritingFullyComplete(card)) return 'completed';
  const slice = card?.narrative_writing;
  const draftStatus = resolveNarrativeDraftStatusKey(slice);
  const plotStatus = resolveNarrativePlotStatusKey(slice);
  if (draftStatus === 'inProgress' || draftStatus === 'started') {
    return 'continueWriting';
  }
  if (plotStatus === 'inProgress' || plotStatus === 'started' || plotStatus === 'completed') {
    return 'startWriting';
  }
  return 'start';
}

export type StandardCardCtaKey = 'start' | 'resume' | 'completed';

export function resolveStandardCardCtaKey(card: FormativeHomeCard): StandardCardCtaKey {
  const status = resolveCardStatusKey(card);
  if (status === 'completed') return 'completed';
  if (status === 'notStarted') return 'start';
  return 'resume';
}

export function continueRouteForActivity(activityKey: string): string {
  if (activityKey === 'malay_unit_5') {
    return buildMalayAiTutorLiveUrl('malay_unit_5');
  }
  if (activityKey === 'malay_jendela_seni_reading') {
    return ROUTES.STUDENT_READING_COMPREHENSION;
  }
  if (activityKey === 'malay_narrative_learning_tutor') {
    return ROUTES.STUDENT_NARRATIVE_WRITING;
  }
  return ROUTES.STUDENT_DASHBOARD;
}

export function activityTitleLabelKey(activityKey: string): string | null {
  if (!isMalayFormativeActivityKey(activityKey)) return null;
  return ACTIVITY_LABEL_KEYS[activityKey];
}

export function resolveActivityDisplayTitle(
  card: FormativeHomeCard,
  lang: string,
  t: (key: string) => string,
): string {
  const d = card.progress?.display;
  if (lang.startsWith('ms') && d?.ms) return d.ms;
  if (d?.en) return d.en;
  const labelKey = activityTitleLabelKey(card.activity_key);
  if (labelKey) return t(labelKey);
  return card.activity_key;
}

/** True when the card has backend-backed formative evidence (never invent). */
export function cardHasFormativeEvidence(card: FormativeHomeCard | null | undefined): boolean {
  if (!card?.has_evidence) return false;
  const level = card.strongest_skill?.current_level;
  if (!level || level === 'not_enough_evidence') return false;
  return true;
}

export type CardStatusKey = 'notStarted' | 'inProgress' | 'completed';

export function resolveCardStatusKey(card: FormativeHomeCard): CardStatusKey {
  if (card.activity_key === 'malay_unit_5' && card.unit5_progress?.completion) {
    const u5Status = String(card.unit5_progress.completion.status || '').toLowerCase();
    if (u5Status === 'completed' || u5Status === 'complete') return 'completed';
    if (
      u5Status === 'in_progress' ||
      u5Status === 'in-progress' ||
      Boolean(card.unit5_progress.continue_topic_id)
    ) {
      return 'inProgress';
    }
  }
  const status = String(card.progress?.status || '').toLowerCase();
  if (status === 'completed' || status === 'complete') return 'completed';
  const completed = Number(card.progress?.completed_units || 0);
  const total = Number(card.progress?.total_units || 0);
  const ratio = card.progress?.completion_ratio;
  if (typeof ratio === 'number' && Number.isFinite(ratio) && ratio >= 1) return 'completed';
  if (total > 0 && completed >= total) return 'completed';
  if (completed > 0 || (typeof ratio === 'number' && ratio > 0) || status === 'in_progress') {
    return 'inProgress';
  }
  return 'notStarted';
}

/**
 * Merge API cards with enabled activities so zero-evidence learners still see
 * honest empty cards (no fabricated mastery).
 */
export function mergeEnabledFormativeHomeCards(
  apiCards: FormativeHomeCard[] | null | undefined,
  features: MalayFormativeFeatureGate,
): FormativeHomeCard[] {
  const byKey = new Map<string, FormativeHomeCard>();
  for (const card of apiCards || []) {
    if (!card?.activity_key) continue;
    if (!activityEnabledForFeatures(card.activity_key, features)) continue;
    byKey.set(card.activity_key, card);
  }

  const ordered: FormativeHomeCard[] = [];
  for (const key of MALAY_FORMATIVE_ACTIVITY_KEYS) {
    if (!activityEnabledForFeatures(key, features)) continue;
    ordered.push(
      byKey.get(key) || {
        activity_key: key,
        has_evidence: false,
        progress: null,
        strongest_skill: null,
        recommended_next_step: null,
        latest_feedback: null,
      },
    );
  }
  return ordered;
}
